class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.config = {
      autorotate: this.dataset.autorotate === 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed) * 1000,
      moveTime: parseFloat(this.dataset.speed), // 100px going to move for
      space: 100, // 100px
      direction: this.dataset.direction || 'left', // Get direction from data attribute
    };

    if (theme.config.mqlSmall) {
      this.initOnMobile();
    }
    else {
      this.initOnDesktop();
    }

    document.addEventListener('matchSmall', () => {
      this.unload();
      this.initOnMobile();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.initOnDesktop();
    });
  }

  initOnDesktop() {
    switch(this.dataset.layout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;

      default:
        this.initMarqueeOptimized();
    }
  }

  initOnMobile() {
    switch(this.dataset.mobileLayout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;
    }
  }

  unload() {
    // Clean up vertical carousel
    if (this.verticalCarouselInterval) {
      clearInterval(this.verticalCarouselInterval);
    }
    
    if (this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy();
      this.removeNextPrevListeners();
    }

    if (this.classList.contains('marquee')) {
      this.classList.remove('marquee');
      this.querySelectorAll('.announcement-slider[aria-hidden]').forEach((slider) => {
        slider.remove();
      });
    }

    if (this.observer && typeof this.observer.unobserve === 'function') {
      this.observer.unobserve(this);
    }
    
    // Remove vertical carousel class
    this.classList.remove('announcement-bar--vertical');
  }

  initCarousel() {
    if (this.dataset.blockCount == 1) {
      return;
    }

    const isVertical = this.config.direction === 'top' || this.config.direction === 'bottom';
    
    // Use custom vertical carousel for top/bottom directions
    if (isVertical) {
      this.initVerticalCarousel();
    } else {
      this.initHorizontalCarousel();
    }

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.onNextPrevClick = this.handleNextPrevClick.bind(this);
    this.prevButton && this.prevButton.addEventListener('click', this.onNextPrevClick);
    this.nextButton && this.nextButton.addEventListener('click', this.onNextPrevClick);
  }

  initHorizontalCarousel() {
    const slider = this.querySelector('.announcement-slider');
    const shouldReverse = this.config.direction === 'right';
    
    setTimeout(() => {
      this.flickity = new Flickity(slider, {
        accessibility: false,
        rightToLeft: shouldReverse ? !theme.config.rtl : theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        setGallerySize: false,
        autoPlay: this.config.autorotate ? this.config.autorotateSpeed : false,
      });
    });
  }

  initVerticalCarousel() {
    this.classList.add('announcement-bar--vertical');
    const slider = this.querySelector('.announcement-slider');
    const slides = Array.from(slider.querySelectorAll('.announcement-slider__slide'));
    
    if (slides.length === 0) return;

    this.currentSlideIndex = 0;
    this.isTransitioning = false;
    
    // Determine direction: 'top' means slides move upward, 'bottom' means slides move downward
    const direction = this.config.direction; // 'top' or 'bottom'

    // Set initial positions
    slides.forEach((slide, index) => {
      slide.style.position = 'absolute';
      slide.style.top = '0';
      slide.style.left = '0';
      slide.style.width = '100%';
      slide.style.opacity = index === 0 ? '1' : '0';
      // Initial position for non-visible slides based on direction
      if (index === 0) {
        slide.style.transform = 'translateY(0)';
      } else {
        // For 'top' direction, new slides come from bottom (100%)
        // For 'bottom' direction, new slides come from top (-100%)
        slide.style.transform = direction === 'top' ? 'translateY(100%)' : 'translateY(-100%)';
      }
      slide.style.transition = 'transform 0.5s ease, opacity 0.5s ease';
    });

    // Auto-rotate if enabled
    if (this.config.autorotate) {
      this.verticalCarouselInterval = setInterval(() => {
        this.moveVerticalSlide('next', direction);
      }, this.config.autorotateSpeed);
    }

    // Store for navigation
    this.verticalSlides = slides;
    this.verticalDirection = direction;
  }

  moveVerticalSlide(navDirection, slideDirection) {
    if (this.isTransitioning) return;
    
    const slides = this.verticalSlides;
    if (!slides || slides.length === 0) return;

    this.isTransitioning = true;
    const currentSlide = slides[this.currentSlideIndex];
    
    let nextIndex;
    if (navDirection === 'next') {
      nextIndex = (this.currentSlideIndex + 1) % slides.length;
    } else {
      nextIndex = (this.currentSlideIndex - 1 + slides.length) % slides.length;
    }
    
    const nextSlide = slides[nextIndex];

    // Animation based on slide direction:
    // 'top' = slides move upward (come from bottom, exit to top)
    // 'bottom' = slides move downward (come from top, exit to bottom)
    
    let exitTransform, enterFromTransform;
    
    if (navDirection === 'next') {
      if (slideDirection === 'top') {
        // Top direction: new slide enters from bottom (100%), current exits to top (-100%)
        exitTransform = 'translateY(-100%)';
        enterFromTransform = 'translateY(100%)';
      } else {
        // Bottom direction: new slide enters from top (-100%), current exits to bottom (100%)
        exitTransform = 'translateY(100%)';
        enterFromTransform = 'translateY(-100%)';
      }
    } else {
      // Previous button reverses the animation
      if (slideDirection === 'top') {
        // Going back: new slide enters from top (-100%), current exits to bottom (100%)
        exitTransform = 'translateY(100%)';
        enterFromTransform = 'translateY(-100%)';
      } else {
        // Going back: new slide enters from bottom (100%), current exits to top (-100%)
        exitTransform = 'translateY(-100%)';
        enterFromTransform = 'translateY(100%)';
      }
    }

    // Set next slide starting position
    nextSlide.style.transform = enterFromTransform;
    nextSlide.style.opacity = '1';

    // Force reflow
    nextSlide.offsetHeight;

    // Animate
    currentSlide.style.transform = exitTransform;
    currentSlide.style.opacity = '0';
    nextSlide.style.transform = 'translateY(0)';

    setTimeout(() => {
      this.currentSlideIndex = nextIndex;
      this.isTransitioning = false;
    }, 500);
  }

  removeNextPrevListeners() {
    this.prevButton && this.prevButton.removeEventListener('click', this.onNextPrevClick);
    this.nextButton && this.nextButton.removeEventListener('click', this.onNextPrevClick);
  }

  handleNextPrevClick(event) {
    event.preventDefault();
    const target = event.currentTarget;
    
    // Check if we're in vertical mode
    if (this.classList.contains('announcement-bar--vertical')) {
      if (target.name === 'next') {
        this.moveVerticalSlide('next', this.verticalDirection);
      } else if (target.name === 'previous') {
        this.moveVerticalSlide('previous', this.verticalDirection);
      }
    } else {
      // Horizontal Flickity carousel
      if (target.name === 'next') {
        this.flickity && this.flickity.next();
      } else if (target.name === 'previous') {
        this.flickity && this.flickity.previous();
      }
    }
  }

  initMarqueeOptimized() {
    setTimeout(() => {
      this.marqueeAnimation();
    });

    // pause when out of view
    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isAnimated && this.classList.remove('marquee--paused');
        }
        else {
          this.isAnimated && this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);

    window.addEventListener('resize', this.marqueeAnimation.bind(this));
  }

  marqueeAnimation() {
    const slider = this.querySelector('.announcement-slider');

    if (slider.scrollWidth > this.clientWidth) {
      if (!this.isAnimated) {
        for (let index = 0; index < 2; index++) {
          const clone = slider.cloneNode(true);
          clone.setAttribute('aria-hidden', true);
          this.appendChild(clone);
        }
        
        this.classList.add('marquee');
        this.style.setProperty('--duration', `${(slider.scrollWidth / this.config.space) * this.config.moveTime}s`);
        this.isAnimated = true;
      }
    }
    else if (this.isAnimated) {
      for (let index = 0; index < 2; index++) {
        this.removeChild(this.lastElementChild);
      }
      this.classList.remove('marquee');
      this.isAnimated = false;
    }
  }

  initMarquee() {
    setTimeout(() => {
      const slider = this.querySelector('.announcement-slider');

      for (let index = 0; index < 10; index++) {
        const clone = slider.cloneNode(true);
        clone.setAttribute('aria-hidden', true);
        this.appendChild(clone);
      }
  
      this.classList.add('marquee');
      this.style.setProperty('--duration', `${(slider.clientWidth / this.config.space) * this.config.moveTime}s`);
    });

    // pause when out of view
    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.classList.remove('marquee--paused');
        }
        else {
          this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);
  }
}
customElements.define('announcement-bar', AnnouncementBar);